// pages/HomePage.tsx
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCourseStore } from '../store/courseStore';
import ContinueCard from '../components/ui/ContinueCard';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

export default function HomePage() {
  const user = useAuthStore((s) => s.user);
  const fetchCourses = useCourseStore((s) => s.fetchCourses);
  const lastPlayed = useCourseStore((s) => s.lastPlayedLesson);
  const courses = useCourseStore((s) => s.courses);

  useEffect(() => {
    if (courses.length === 0) fetchCourses();
  }, [courses.length, fetchCourses]);

  const displayName = user?.displayName || user?.phoneNumber || 'Learner';
  const firstName = displayName.split(' ')[0];

  return (
    <div className="min-h-screen gradient-home safe-top safe-bottom">
      <div className="px-5 pt-12 pb-8 max-w-lg mx-auto page-enter">

        {/* Greeting */}
        <div className="mb-6 animate-fade-up">
          <h1 className="text-3xl font-extrabold text-surface-900 tracking-tight">
            {getGreeting()},
          </h1>
          <h2 className="text-3xl font-extrabold text-mint-600 tracking-tight">
            {firstName}! 👋
          </h2>
          <p className="text-surface-300 text-sm mt-2">
            What do you want to do today?
          </p>
        </div>

        {/* Continue Card */}
        {lastPlayed && (
          <div className="mb-6">
            <ContinueCard
              videoId={lastPlayed.videoId}
              lessonTitle={lastPlayed.videoTitle}
              chapterName={lastPlayed.chapterName}
              subjectName={lastPlayed.subjectName}
              chapterId={lastPlayed.chapterId}
              subjectId={lastPlayed.subjectId}
            />
          </div>
        )}

        {/* Main Two Cards */}
        <div className="grid grid-cols-2 gap-4">
          {/* Course Card */}
          <Link
            to="/course"
            className="touch-card rounded-3xl overflow-hidden shadow-card animate-fade-up-delay"
          >
            <div className="gradient-mint p-5 h-56 flex flex-col justify-between relative">
              {/* Decorative */}
              <div className="absolute top-3 right-3 w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
                </svg>
              </div>
              
              <div className="flex-1 flex items-center justify-center">
                <span className="text-5xl">📘</span>
              </div>
              
              <div>
                <h3 className="text-white font-bold text-xl">Course</h3>
                <p className="text-white/70 text-xs mt-1 leading-relaxed">
                  Structured learning path
                </p>
              </div>
            </div>
          </Link>

          {/* Explore Card */}
          <Link
            to="/explore"
            className="touch-card rounded-3xl overflow-hidden shadow-card animate-fade-up-delay-2"
          >
            <div className="gradient-lavender p-5 h-56 flex flex-col justify-between relative">
              {/* Decorative */}
              <div className="absolute top-3 right-3 w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </div>
              
              <div className="flex-1 flex items-center justify-center">
                <span className="text-5xl">🔥</span>
              </div>
              
              <div>
                <h3 className="text-white font-bold text-xl">Explore</h3>
                <p className="text-white/70 text-xs mt-1 leading-relaxed">
                  Short videos, always new
                </p>
              </div>
            </div>
          </Link>
        </div>

        {/* Logout (small, bottom) */}
        {user && (
          <div className="mt-10 text-center animate-fade-up">
            <button
              onClick={() => useAuthStore.getState().logout()}
              className="text-xs text-surface-300 hover:text-coral-500 transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
